import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './cos-icon.js';
import './cos-button.js';
import '../design-system/cos-input.js';
import {
	IMPERSONATION_EVENT,
	IMPERSONATION_READONLY_EVENT,
	allowImpersonationWrites,
	clearImpersonationSession,
	currentImpersonationSession,
	refreshImpersonationSession,
	stopImpersonation,
	type ImpersonationSession,
} from '../services/impersonation.js';
import { userMessage } from '../utils/errorMessages.js';

const TICK_MS = 10_000;
// The transient "session ended" notice (below) stays up long enough to be
// read, then clears itself — the banner it replaces was non-dismissable
// while LIVE, but there's nothing left to guard against once the session
// is already over.
const EXPIRED_NOTICE_MS = 8_000;

/**
 * <cos-impersonate-dialog>'s persistent companion — docs/impersonation-plan.md
 * step 5. Self-contained: mounted unconditionally in cos-app-shell (like
 * cos-notification-bell), renders nothing when no session is held, and
 * tracks the session itself via services/impersonation.ts rather than
 * being fed state as a prop — so it works the same way regardless of
 * which surface started the session.
 *
 * Cannot be dismissed while a session is live (the one part of the
 * removed legacy version worth keeping) — there is deliberately no close
 * button anywhere in this template for that state.
 *
 * Also owns the "your change wasn't saved — this session is read-only"
 * surfacing (docs/impersonation-plan.md "Read-only enforcement"): any
 * write refused anywhere in the app broadcasts
 * IMPERSONATION_READONLY_EVENT, which this component listens for
 * regardless of which page triggered it, and turns into an inline prompt
 * to switch to "allow changes" (with its own required reason) rather than
 * a silent, confusing 403.
 */
@customElement('cos-impersonation-banner')
export class CosImpersonationBanner extends LitElement {
	@state() private _session: ImpersonationSession | null = null;
	@state() private _now = Date.now();
	// Set the moment a live session disappears because its clock ran out
	// (never because someone dismissed it — there's no such control).
	// Drives a short-lived "session ended" notice in place of the banner.
	@state() private _justExpired = false;
	@state() private _showAllowWrites = false;
	@state() private _allowWritesReason = '';
	@state() private _allowWritesSubmitting = false;
	@state() private _allowWritesError = '';
	@state() private _stopping = false;

	private _tickTimer: ReturnType<typeof setInterval> | null = null;
	private _expiredNoticeTimer: ReturnType<typeof setTimeout> | null = null;

	private _onSessionChanged = (e: Event): void => {
		const session = (e as CustomEvent<{ session: ImpersonationSession | null }>).detail.session;
		this._applySession(session);
	};

	private _onReadonlyBlocked = (): void => {
		if (!this._session || this._session.write_mode !== 'read') return;
		this._showAllowWrites = true;
	};

	connectedCallback(): void {
		super.connectedCallback();
		const stored = currentImpersonationSession();
		this._applySession(stored);
		// Reconcile against the server — but ONLY when there's already a
		// locally-held session to reconcile. This component is mounted
		// unconditionally for every signed-in user on every page (the
		// notification-bell precedent), so calling this unconditionally
		// would mean a network round trip on every single page load for
		// every user, forever, to guard against the rare case of a
		// session ending server-side while this tab sat in the
		// background — worth checking when we THINK we're impersonating,
		// not worth paying for otherwise.
		if (stored) void refreshImpersonationSession();
		window.addEventListener(IMPERSONATION_EVENT, this._onSessionChanged);
		window.addEventListener(IMPERSONATION_READONLY_EVENT, this._onReadonlyBlocked);
		this._tickTimer = setInterval(() => this._onTick(), TICK_MS);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener(IMPERSONATION_EVENT, this._onSessionChanged);
		window.removeEventListener(IMPERSONATION_READONLY_EVENT, this._onReadonlyBlocked);
		if (this._tickTimer) clearInterval(this._tickTimer);
		if (this._expiredNoticeTimer) clearTimeout(this._expiredNoticeTimer);
	}

	private _applySession(session: ImpersonationSession | null): void {
		this._session = session;
		this._now = Date.now();
		if (session) {
			this._justExpired = false;
			this._showAllowWrites = false;
			this._allowWritesReason = '';
			this._allowWritesError = '';
		}
	}

	private _onTick(): void {
		this._now = Date.now();
		if (this._session && this._remainingMs() <= 0) {
			// The clock ran out. Drop out locally rather than leaving the
			// header attached to a request that the server will reject
			// anyway (its own expires_at check) — a rejected write with no
			// explanation is exactly the "requests start failing
			// mysteriously" the plan calls out. clearImpersonationSession()
			// doesn't touch the server (see its own doc comment); the row
			// there is already past its own expiry.
			clearImpersonationSession();
			this._session = null;
			this._justExpired = true;
			if (this._expiredNoticeTimer) clearTimeout(this._expiredNoticeTimer);
			this._expiredNoticeTimer = setTimeout(() => { this._justExpired = false; }, EXPIRED_NOTICE_MS);
		}
	}

	private _remainingMs(): number {
		if (!this._session) return 0;
		return new Date(this._session.expires_at).getTime() - this._now;
	}

	private _remainingLabel(): string {
		const ms = this._remainingMs();
		if (ms <= 0) return 'Session ending…';
		const totalMinutes = Math.ceil(ms / 60_000);
		if (totalMinutes <= 1) return 'Less than a minute left';
		return `${totalMinutes} min left`;
	}

	private async _stop(): Promise<void> {
		if (this._stopping) return;
		this._stopping = true;
		try {
			await stopImpersonation();
		} finally {
			// Whatever the server said, the local session is gone (see
			// stopImpersonation's own best-effort doc comment) — reload so
			// the whole page re-fetches under the real identity rather than
			// leaving stale, target-scoped data on screen.
			window.location.reload();
		}
	}

	private _openAllowWrites(): void {
		this._showAllowWrites = true;
		this._allowWritesError = '';
	}

	private async _submitAllowWrites(): Promise<void> {
		const reason = this._allowWritesReason.trim();
		if (!reason || this._allowWritesSubmitting) return;
		this._allowWritesSubmitting = true;
		this._allowWritesError = '';
		try {
			const session = await allowImpersonationWrites(reason);
			this._applySession(session);
		} catch (err) {
			this._allowWritesError = userMessage(err).description;
		} finally {
			this._allowWritesSubmitting = false;
		}
	}

	static styles = css`
		:host {
			display: block;
		}
		.bar {
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			z-index: var(--cos-z-toast);
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: var(--cos-space-3);
			padding: var(--cos-space-2) var(--cos-space-4);
			background: var(--cos-status-warning-wash);
			color: var(--cos-status-warning-text);
			font-size: var(--cos-font-size-sm);
		}
		.bar strong {
			font-weight: var(--cos-font-weight-semibold);
		}
		.spacer {
			flex: 1;
		}
		.mode {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			padding: 2px 8px;
			border-radius: var(--cos-radius-pill);
			background: color-mix(in srgb, var(--cos-status-warning-text) 14%, transparent);
			font-weight: var(--cos-font-weight-medium);
			white-space: nowrap;
		}
		.time {
			white-space: nowrap;
		}
		.prompt {
			position: fixed;
			top: 100%;
			left: 0;
			right: 0;
			display: flex;
			flex-wrap: wrap;
			align-items: flex-end;
			gap: var(--cos-space-3);
			padding: var(--cos-space-3) var(--cos-space-4);
			background: var(--cos-surface);
			border-bottom: 1px solid var(--cos-border);
			box-shadow: var(--cos-shadow-lg);
		}
		.prompt-field {
			flex: 1;
			min-width: 220px;
		}
		.prompt-error {
			flex-basis: 100%;
			color: var(--cos-status-danger-text);
			font-size: var(--cos-font-size-sm);
		}
		.expired-note {
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			z-index: var(--cos-z-toast);
			padding: var(--cos-space-2) var(--cos-space-4);
			background: var(--cos-surface-raised);
			color: var(--cos-text-secondary);
			font-size: var(--cos-font-size-sm);
			text-align: center;
		}
	`;

	render() {
		if (!this._session) {
			if (this._justExpired) {
				return html`
					<div class="expired-note" role="status" aria-live="polite">
						Impersonation session ended: the 30-minute time limit was reached.
					</div>
				`;
			}
			return nothing;
		}
		const s = this._session;
		return html`
			<div class="bar" role="status" aria-live="polite">
				<cos-icon name="user-cog" size="16"></cos-icon>
				<span>Viewing as <strong>${s.target.name}</strong></span>
				<span class="mode">${s.write_mode === 'write' ? 'Changes allowed' : 'Read-only'}</span>
				<span class="time">${this._remainingLabel()}</span>
				${s.write_mode === 'read' && !this._showAllowWrites
					? html`<cos-button variant="link" size="sm" @click=${this._openAllowWrites}>Allow changes…</cos-button>`
					: nothing}
				<span class="spacer"></span>
				<cos-button size="sm" variant="secondary" ?loading=${this._stopping} @click=${() => void this._stop()}>
					Stop impersonating
				</cos-button>
			</div>
			${this._showAllowWrites && s.write_mode === 'read'
				? html`
						<div class="prompt">
							<div class="prompt-field">
								<cos-input
									label="Why does this session need to make changes?"
									required
									size="sm"
									.value=${this._allowWritesReason}
									@cos:input=${(e: CustomEvent<{ value: string }>) => { this._allowWritesReason = e.detail.value; }}
								></cos-input>
							</div>
							<cos-button
								size="sm"
								variant="primary"
								?disabled=${this._allowWritesReason.trim().length === 0}
								?loading=${this._allowWritesSubmitting}
								@click=${() => void this._submitAllowWrites()}
							>Allow changes</cos-button>
							<cos-button size="sm" variant="ghost" @click=${() => { this._showAllowWrites = false; }}>Cancel</cos-button>
							${this._allowWritesError ? html`<div class="prompt-error">${this._allowWritesError}</div>` : nothing}
						</div>
					`
				: nothing}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-impersonation-banner': CosImpersonationBanner;
	}
}
